/** @jsxImportSource @emotion/react */
import { Header, TruncatedString } from '@quickbit/backoffice-core-frontend';
import {
  Icon,
  SearchInput,
  Option,
  Table,
  Typography,
  useForm,
  useTheme,
  UtilityButton,
  UtilitySelect,
  DateInput,
  Pagination,
  removeScaleFromMoney,
} from '@quickbit/qb-design-system';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn, useDebounce, useSessionStorage } from 'react-use';
import { validate as validateUUID } from 'uuid';
import {
  exportTransactions,
  searchTransactions,
  TransactionStatus,
  TransactionType,
  type SearchTransactionsFilters,
  type SearchTransactionsResponse,
} from 'components/Transactions/api';

const PAGE_SIZE = 16;

const TYPE_OPTIONS = Object.keys(TransactionType).map((key) => ({
  value: key,
  label: TransactionType[key as keyof typeof TransactionType],
}));

const STATUS_OPTIONS = Object.keys(TransactionStatus).map((key) => ({
  value: key,
  label: TransactionStatus[key as keyof typeof TransactionStatus],
}));

export const TransactionList = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const headers = [
    { label: 'Created At', cellKey: 'createdAt', sort: false },
    { label: 'ID', cellKey: 'id', sort: false },
    { label: 'Type', cellKey: 'type', sort: false },
    { label: 'Amount', cellKey: 'amount', sort: false },
    { label: 'Euro Amount', cellKey: 'euroAmount', sort: false },
    {
      label: 'Nexus Transaction Code',
      cellKey: 'nexusTransactionCode',
      sort: false,
    },
    { label: 'Status', cellKey: 'status', sort: false },
  ];

  const [filterStore, setFilterStore] = useSessionStorage<{
    filters: SearchTransactionsFilters;
    pagination: {
      page: number;
      size: number;
    };
  } | null>('qbTransactionsFilterStore');

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<SearchTransactionsFilters>(
    {
      uuid: filterStore?.filters.uuid ?? undefined,
      userId: filterStore?.filters.userId ?? undefined,
      createdFrom: filterStore?.filters.createdFrom ?? undefined,
      createdTo: filterStore?.filters.createdTo ?? undefined,
      transactionType: filterStore?.filters.transactionType ?? undefined,
      currency: filterStore?.filters.currency ?? undefined,
      nexusTransactionCode:
        filterStore?.filters.nexusTransactionCode ?? undefined,
      status: filterStore?.filters.status ?? undefined,
      travelRuleTransactionId:
        filterStore?.filters.travelRuleTransactionId ?? undefined,
    },
    {}
  );

  const [searchResult, setSearchStore] =
    useSessionStorage<SearchTransactionsResponse>('transactionsSearchResult', {
      transactions: [],
      aggregatedData: { count: 0 },
    });

  const normalizedFilters: SearchTransactionsFilters = useMemo(
    () => ({
      ...fields,
      currency: fields.currency ? fields.currency.toUpperCase() : undefined,
      uuid: fields.uuid && validateUUID(fields.uuid) ? fields.uuid : undefined,
      userId:
        fields.userId && validateUUID(fields.userId)
          ? fields.userId
          : undefined,
      travelRuleTransactionId:
        fields.travelRuleTransactionId &&
        validateUUID(fields.travelRuleTransactionId)
          ? fields.travelRuleTransactionId
          : undefined,
    }),
    [fields]
  );

  const [{ loading }, search] = useAsyncFn(async () => {
    const appliedFilters = {
      filters: normalizedFilters,
      pagination: {
        page: page,
        size: PAGE_SIZE,
      },
    };

    const data = await searchTransactions(appliedFilters);
    setFilterStore(appliedFilters);
    setSearchStore(data);
    return data;
  }, [page, normalizedFilters]);

  const [{ loading: exportLoading }, exportTransactionsFn] =
    useAsyncFn(async () => {
      const blob = await exportTransactions(normalizedFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }, [normalizedFilters]);

  useDebounce(
    () => {
      void search();
    },
    500,
    [search]
  );
  const rows = useMemo(() => {
    return searchResult.transactions.map(
      ({
        id,
        amount,
        euroAmount,
        nexusTransactionCode,
        type,
        createdAt,
        status,
      }) => ({
        value: {
          createdAt: new Date(createdAt).toLocaleString(),
          id: <TruncatedString text={id} />,
          uuid: id, // for navigation
          type,
          status,
          amount: `${removeScaleFromMoney(amount)} ${amount.currencyCode}`,
          euroAmount: `${removeScaleFromMoney(euroAmount)}`,
          nexusTransactionCode: nexusTransactionCode ? (
            <TruncatedString text={nexusTransactionCode} />
          ) : undefined,
        },
      })
    );
  }, [searchResult]);

  const handleResetFilters = () => {
    setPage(1);
    setters.set({
      createdFrom: undefined,
      createdTo: undefined,
      transactionType: undefined,
      userId: undefined,
    });
  };

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(1, auto)',
            alignItems: 'center',
            gap: spacing.xxs,
          }}
        >
          <Typography variant="subtitle">Transactions</Typography>
        </div>
      </Header>

      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'auto auto auto auto auto 1fr',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <SearchInput
          placeholder="Transaction ID"
          value={fields.uuid ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.uuid?.set(e.target.value);
          }}
          error={
            fields.uuid !== undefined &&
            fields.uuid !== '' &&
            !validateUUID(fields.uuid)
          }
        />
        <SearchInput
          placeholder="User ID"
          value={fields.userId ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.userId?.set(e.target.value);
          }}
          error={
            fields.userId !== undefined &&
            fields.userId !== '' &&
            !validateUUID(fields.userId)
          }
        />
        <SearchInput
          placeholder="Nexus Transaction Code"
          value={fields.nexusTransactionCode ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.nexusTransactionCode?.set(e.target.value);
          }}
        />
        <SearchInput
          placeholder="Currency"
          value={fields.currency ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.currency?.set(e.target.value);
          }}
        />
        <SearchInput
          placeholder="Travel Rule ID"
          value={fields.travelRuleTransactionId ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.travelRuleTransactionId?.set(e.target.value);
          }}
          error={
            fields.travelRuleTransactionId !== undefined &&
            fields.travelRuleTransactionId !== '' &&
            !validateUUID(fields.travelRuleTransactionId)
          }
        />
      </div>
      <div
        css={{
          marginTop: spacing.xxs,
          marginBottom: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: 'repeat(99, auto) 1fr',
          alignItems: 'center',
          gap: spacing.xxxs,
          textWrap: 'nowrap',
        }}
      >
        <DateInput
          placeholder="All time"
          type="range"
          value={[
            fields.createdFrom ? new Date(fields.createdFrom) : null,
            fields.createdTo ? new Date(fields.createdTo) : null,
          ]}
          onChange={(e: [Date | null, Date | null]) => {
            setPage(1);
            setters.createdFrom?.set(e[0]?.toISOString());
            setters.createdTo?.set(e[1]?.toISOString());
          }}
          withTime
        />
        <UtilitySelect
          label={
            fields.transactionType
              ? TYPE_OPTIONS.find(
                  (typeOption) => typeOption.value === fields.transactionType
                )?.label
              : 'Type'
          }
          listTitle="Type"
          options={TYPE_OPTIONS}
          value={
            fields.transactionType
              ? TYPE_OPTIONS.find(
                  (typeOption) => typeOption.value === fields.transactionType
                ) || null
              : null
          }
          onSelect={(data) => {
            setPage(1);
            setters.transactionType?.set((data as Option | undefined)?.value);
          }}
        />
        <UtilitySelect
          label={
            fields.status
              ? STATUS_OPTIONS.find(
                  (statusOption) => statusOption.value === fields.status
                )?.label
              : 'Status'
          }
          listTitle="Status"
          options={STATUS_OPTIONS}
          value={
            fields.status
              ? STATUS_OPTIONS.find(
                  (statusOption) => statusOption.value === fields.status
                ) || null
              : null
          }
          onSelect={(data) => {
            setPage(1);
            setters.status?.set((data as Option | undefined)?.value);
          }}
        />
        <UtilityButton
          variant="text"
          text="Reset filters"
          onClick={handleResetFilters}
        />
        <UtilityButton
          variant="secondary"
          text="Refresh"
          onClick={search}
          disabled={loading}
        />
        <UtilityButton
          variant="secondary"
          text="Export"
          loading={exportLoading}
          disabled={exportLoading}
          onClick={exportTransactionsFn}
        />
        {loading && (
          <Icon
            name="spinner"
            className={{ svg: { color: palette.grey.default } }}
          />
        )}
      </div>
      <Table
        headers={headers}
        rows={rows}
        // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
        onRowClick={(row) => navigate(`${row.uuid}`)}
      />
      <div css={{ marginTop: spacing.xxs }}>
        <Pagination
          currentPage={page}
          onChange={setPage}
          numberOfElements={searchResult.aggregatedData.count}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};
